import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import isEqual from 'react-fast-compare';
import { Helmet } from 'react-helmet';
import { prefillPayloadSelector } from 'ctm-selectors/selectors/user';

import {
    apiAction,
    elementRulesStateToProps,
    get,
    logException,
    logMessage,
    logToNR,
    mapStateToArgs,
    nestFlatQuery,
    pageContext,
    rulesCache,
    setupExperimentRunner,
    startOnlineCheck,
    subscribeCustomerAccountEvent
} from 'ctm-util-functions';

import { experimentIdSelector, locationQuerySelector, pageNameSelector, splitTrafficIdSelector, verticalNameSelector } from 'ctm-selectors';
import pageNames from 'ctm-selectors/selectors/tracking/verticals/page_names';
import pageStyles from './page.css';

/* Require page global styles/reset */
require('./global.css');

const getPage = (vertical, name) => get(pageNames, `${vertical}.${name}`, name).replace(/_/g, ' ');

const getActionStep = (state) => {
    const page = getPage(state.vertical, state.pageName.toLowerCase());
    return state.pageName.startsWith(state.vertical) ? page : `${state.vertical} ${page}`;
};

const saveJourneyIntoSessionStorage = (journeyId) => {
    sessionStorage.setItem('journeyId', journeyId);
};

const getJourneyFromSessionStorage = () => {
    return sessionStorage.getItem('journeyId');
};

class _Page extends Component {

    constructor(props) {
        super(props);
        this.state = {
            caSignout: false
        };
    }

    // eslint-disable-next-line camelcase
    UNSAFE_componentWillMount = () => {
        const { dispatch, isSSR, location, pageSchema } = this.props;

        // Set the runExperiment function on window
        if (!isSSR) {
            setupExperimentRunner(dispatch);
        }

        // Set user_anonymous_id and user_id to Redux store as this is only
        // called once at application start. #anonymous_prefill
        dispatch({
            type: 'SET_VALUE',
            value: sessionStorage.getItem('user_anonymous_id'),
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.userAnonymousId'
        });
        dispatch({
            type: 'SET_VALUE',
            value: sessionStorage.getItem('user_id'),
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.userId'
        });
        dispatch({
            type: 'SET_VALUE',
            value: sessionStorage.getItem('experience-id'),
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.experienceId'
        });

        // Load all page data such as preload and onMount api calls.
        dispatch({
            type: 'LOAD_PAGE_DATA',
            pageToLoad: pageSchema,
            location
        });
    };

    componentDidMount = () => {
        const { dispatch, pageState, pageSchema, location, experimentId, vertical } = this.props;
        const leavePageWarningEnabled = get(pageSchema, 'leavePageWarningEnabled', get(pageState, 'serverConfig.leavePageWarningEnabled'));

        this.setPreloadStateFromQS();

        // Add onPageLeave listener, so we can warn the user they are about to leave our site.
        // @todo: move this bit to componentWillUnmount()
        if (typeof window !== 'undefined' && leavePageWarningEnabled === true && pageState.pageName !== 'verify' && pageState.pageName !== 'quotes_expired') {
            // ADO-30338: Log refresh - this will log after the refresh event has happened
            const pageWasAccessedViaReload = window.performance
                .getEntriesByType('navigation')
                .map((nav) => nav.type)
                .includes('reload');
            if (getJourneyFromSessionStorage()) {
                if (pageWasAccessedViaReload) {
                    // If we hit this bit, it would've been after the reload, so we want to get the original journeyId before the reload.
                    const journeyId = getJourneyFromSessionStorage();
                    const msg = `Page now reloaded.  Vertical: ${vertical}.  JourneyId: ${journeyId}.  UserAgent: ${window.navigator.userAgent}`;

                    logMessage(msg);

                    logToNR(msg, 'INFO');
                }
            }

            window.addEventListener('beforeunload', this.onUnload);
        }

        if (typeof window !== 'undefined') {
            window.addEventListener('ca_signout', (e) => {
                this.setState(
                    {
                        caSignout: true
                    },
                    () => window.location.reload()
                );
            });
            window.addEventListener('ca_modal_closed', (e) => {
                const data = {
                    event: 'PAGE_VIEW_EVENT',
                    actionStep: getActionStep(this.props),
                    experimentId,
                    vertical
                };
                recordGTM('PAGE_VIEW_EVENT', data);
            });
        }

        // Start the Online status handler
        startOnlineCheck(dispatch);

        // Subscribe to customer account event to pull profile data
        subscribeCustomerAccountEvent(dispatch);
    };

    componentWillUnmount = () => {
        const { experimentId, vertical } = this.props;
        window.removeEventListener('ca_signout', (e) => {
            this.setState(
                {
                    caSignout: true
                },
                () => window.location.reload()
            );
        });
        window.removeEventListener('ca_modal_closed', (e) => {
            const data = {
                event: 'PAGE_VIEW_EVENT',
                actionStep: getActionStep(this.props),
                experimentId,
                vertical
            };
            recordGTM('PAGE_VIEW_EVENT', data);
        });
    };

    /**
     * Determine if the component should re-render based on the nextProps
     * We have to use this as we're mapping objects onto props and the default shallow equal
     * doesn't work in this case and always causes a re-render on state changes.
     * Using react-fast-compare from https://github.com/FormidableLabs/react-fast-compare
     * @param  {Object} nextProps  The incoming set of props
     * @returns {Boolean}           true if the component should re-render
     */
    shouldComponentUpdate = (nextProps) => {
        return !isEqual(nextProps, this.props);
    };

    handlePrefillEvents = () => {
        const { dispatch } = this.props;
        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.userAnonymousId',
            value: sessionStorage.getItem('user_anonymous_id')
        });

        dispatch({
            type: 'SET_VALUE',
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.experienceId',
            value: 'carPrefill2024'
        });

        dispatch({
            type: 'LOAD_PAGE_DATA',
            location: {
                hash: '',
                pathname: '/car-insurance/journey/prefill_check',
                search: ''
            },

            pageToLoad: {
                allowRedirectBetweenPages: true,
                api: {
                    onMount: {
                        url: 'journey',
                        type: 'GET',
                        failureType: 'fatal',
                        requestAction: 'GET_PAYLOAD',
                        restArgs: [
                            {
                                varPath: 'prefill',
                                value: 'check'
                            },

                            {
                                queryParam: 'anonymous_id',
                                identifier: 'vertical.quote.form.helpers.prefill.userAnonymousId'
                            },

                            {
                                queryParam: 'user_id',
                                identifier: 'vertical.quote.form.helpers.prefill.userId'
                            },

                            {
                                queryParam: 'experiment_id',
                                identifier: 'vertical.quote.form.helpers.prefill.experienceId'
                            }
                        ],
                        preRequestActions: [
                            {
                                type: 'SET_PAGE_VALUE',
                                identifier: 'showPleaseWait',
                                value: true
                            }
                        ],
                        skipRetryOnError: false,
                        responseActions: [
                            {
                                type: 'SET_MANUAL_LOOKUP_DATA',
                                identifier: 'hasJourneys',
                                splitTrafficId: null
                            },
                            {
                                type: 'SET_LOGGING_IDENTIFY',
                                splitTrafficId: null
                            },
                            {
                                type: 'TRACK_INITIAL_FORM',
                                splitTrafficId: null
                            },
                            {
                                type: 'PROGRESS_TO_PAGE',
                                pageName: 'prefill_verify',
                                splitTrafficId: null
                            }
                        ]
                    }
                },
                name: 'prefill_check',
                path: 'car-insurance/journey/prefill_check',
                title: 'Welcome'
            },
            stateKey: 'vertical.quote',
            identifier: 'helpers.prefill.isAnonymous',
            value: true
        });
    };

    leavePageDialog = () => {
        const { pageState, pageSchema, vertical } = this.props;
        const leavePageWarningEnabled = get(pageSchema, 'leavePageWarningEnabled', get(pageState, 'serverConfig.leavePageWarningEnabled'));
        this.setPreloadStateFromQS();

        if (typeof window !== 'undefined' && leavePageWarningEnabled === true && pageState.pageName !== 'verify' && pageState.pageName !== 'quotes_expired') {
            // ADO-30338: Log refresh - this will log after the refresh event has happened
            const pageWasAccessedViaReload = window.performance
                .getEntriesByType('navigation')
                .map((nav) => nav.type)
                .includes('reload');

            if (getJourneyFromSessionStorage()) {
                if (pageWasAccessedViaReload) {
                    // If we hit this bit, it would've been after the reload, so we want to get the original journeyId before the reload.
                    const journeyId = getJourneyFromSessionStorage();
                    const msg = `Page now reloaded.  Vertical: ${vertical}.  JourneyId: ${journeyId}.  UserAgent: ${window.navigator.userAgent}`;

                    logMessage(msg);

                    logToNR(msg, 'INFO');
                }
            }

            window.addEventListener('beforeunload', this.onUnload);
        }
    };

    /**
     * Update page snippets, so we have the correct code injected.
     * i.e the stuff we need and not the stuff we don't.
     */
    // eslint-disable-next-line camelcase
    UNSAFE_componentWillUpdate = () => {
        const updatesPageSnippets = get(pageContext.getProps().models, 'snippetModel.updatesPageSnippets');
        if (typeof updatesPageSnippets === 'function') {
            updatesPageSnippets();
        }
    };

    /**
     * The page has updated, dispatch an action to reset the transitioningToNewPage state value
     *
     * Also fire any onMount api calls here - this only happens for pages other than the first page as this is only
     * called for mounted components
     * @param {Object} prevProps - The previous set of props
     */
    componentDidUpdate = (prevProps) => {
        const { isRedirecting, isRouteChanged, location, reloadPage, pageSchema, dispatch, pageState, journeyId, brandCode } = this.props;
        const { api, onMountActions, name, allowRedirectBetweenPages } = pageSchema;
        this.leavePageDialog();

        if (isRedirecting || !isRouteChanged) {
            // if this gets called during a redirect, don't do anything as this is unnecessary for pages that won't be displayed
            return;
        }

        if (getJourneyFromSessionStorage() == null && journeyId != null) {
            saveJourneyIntoSessionStorage(journeyId);
        }

        if (location.pathname !== prevProps.location.pathname || reloadPage) {
            const pageSchemaModel = pageContext.getProps().models.pageSchemaModel;
            // dispatch the actions set when leaving the previous page.
            const prevPageSchema = prevProps.pageSchema;
            if (prevPageSchema.onLeave) {
                const isLaterPageInSchema = pageSchemaModel.isLaterPageInSchema(prevProps.pageState.pageName, pageState.path);
                if (!isLaterPageInSchema && prevPageSchema.onLeave.toPrevRouteActions) {
                    const actions = prevPageSchema.onLeave.toPrevRouteActions;
                    // @todo: forEach....
                    actions.forEach((action) => dispatch(action));
                }
            }

            // Check if the page from the previous state is set as previous in the page schema.
            const isLastPagePrevPageInSchema = pageSchemaModel.isPreviousPageInSchema(name, prevPageSchema.path);

            if (isLastPagePrevPageInSchema || reloadPage || allowRedirectBetweenPages) {
                if (onMountActions) {
                    onMountActions.forEach((action) => dispatch(action));
                }
                // Dispatch the onMount actions only when coming from a previous page set in the schema.

                if (api && api.onMount) {
                    // TODO: FIX THIS
                    // I DO NOT LIKE THIS IDEA??!?!!? CAN THIS NOT DISPATCH THE RUN API ON MOUNT FOR PAGE?

                    // For queryStringArgs from api onMount schema - eg url.com?var=1&var=2
                    const { queryStringArgs } = api.onMount;
                    if (queryStringArgs) {
                        api.onMount.queryStringValues = mapStateToArgs(pageContext.getProps().store.getState(), queryStringArgs);
                    }
                    // For restArgs from api onMount schema - eg url.com/MAZDA/RX8
                    let restArgs = api.onMount.restArgs;
                    if (restArgs) {
                        restArgs = mapStateToArgs(pageContext.getProps().store.getState(), restArgs);
                    }

                    const currentState = pageContext.getProps().store.getState();
                    const lastViewedPage = currentState.getIn(['page', 'lastViewedPage']);
                    const prefill = prefillPayloadSelector(currentState);
                    const isBrandCodeChoosi = brandCode === 'choo';

                    // For anonymous prefill we want to be updating the lastViewedPage when the user routes backwards.
                    // If we are navigating backwards dispatch an action to update the journey manually without retriggering
                    // the onMount actions.
                    //
                    // If moving forwards through the journey or coming from confirmation / results page (via edit):
                    const pagesToPerformNormalOnMountActions = ['confirmation', 'results', 'modifications', 'annual_kms', 'cover_type', 'car_colour', 'verify'];

                    if (isLastPagePrevPageInSchema || pagesToPerformNormalOnMountActions.includes(lastViewedPage)) {
                        // instead of relying on browser events for detecting back button,
                        // we are able to tell which page everest is going to next
                        // if we are leaving the car_colour page, and we are not progessing through the journey

                        if (lastViewedPage === 'car_colour' && (pageState.pageName !== 'cover_type' && pageState.pageName !== 'car_series') && prefill && !isBrandCodeChoosi) {
                            // car_colour is a special case as it is the first eligible prefill page
                            // we need to manually dispatch the below actions that handles the redirect back to prefill_check
                            // when the user clicks browser 'back' button
                            this.handlePrefillEvents();
                        } else {
                            dispatch(apiAction(api.onMount, pageState, restArgs, api.onMount.baseUrl));
                        }
                    } else {
                        // Routing backwards - this only calls the initial endpoint and not any other on mount actions
                        // for the purposes of updating the lastViewedPage
                        const apiSchema = {
                            url: 'journey/initial',
                            type: 'POST',
                            requestAction: 'POST_PAYLOAD',
                            requestPayload: 'journey',
                            failureType: 'non-fatal'
                        };
                        dispatch(apiAction(apiSchema, pageContext.getProps().pageState, [], apiSchema.baseUrlName, currentState));
                    }
                }
            }
        }

        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'transitioningToNewPage',
            value: false
        });
        dispatch({
            type: 'SET_PAGE_VALUE',
            identifier: 'doReload',
            value: false
        });

        // The subscription is not triggered in some cases like A/B testing on component mounted
        // So this triggers the subscription depends on the props changed.
        subscribeCustomerAccountEvent(dispatch);

        this.redirectIfNeeded();
    };

    /**
     * Enables the unload warning
     * Note: this only works in some browsers. Custom messages are now only supported by IE and Edge.
     * @see: https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
     * @param {Object} event The window's event object.
     */
    onUnload = (event) => {
        // ADO-30338: Check when the user is about to close the tab
        const { vertical, pageState, is11MonthHandover } = this.props;
        const journeyId = getJourneyFromSessionStorage();
        const msg = `User closing window or attempting refresh.  Vertical: ${vertical}.  JourneyId: ${journeyId}.  Useragent: ${window.navigator.userAgent}`;

        if (journeyId != null) {
            logMessage(msg);

            logToNR(msg, 'INFO');
        }

        if (is11MonthHandover) {
            return;
        }

        // eslint-disable-next-line no-undef
        const { caSignout } = this.state;
        if (caSignout) return;

        const returnText = 'Changes that you made may not be saved. Are you sure you want to leave this page?';
        // eslint-disable-next-line no-param-reassign
        event.returnValue = returnText;
    };

    /**
     * The dev toolbar at the bottom.
     * @returns {Node} A DevTool component
     */
    getDevTools = () => {
        // eslint-disable-next-line global-require
        const DevTool = require('../dev_tool/dev_tool').DevTool;
        return <DevTool key={'devTool'} />;
    };

    /**
     * Build the list of children to render, excluding layout area keys if they are defined via rules
     *
     * This function prevents selected layout areas from rendering while the please wait component is overlaid. This
     * happens while the application is waiting for a response from the backend or on page load.
     *
     * We can specify which layout area to 'hide' in a specific rule definition, for example:
     *
     * "rules": [{
     *      "controls": "layoutKeyVisibility",
     *      "rule": "showPleaseWait",
     *      "props": {"layoutKey":  "content" }
     *  }]
     *
     * That rule controls layout key visibility - and will set a 'showPleaseWait' boolean value in the application's
     * state. We also provide the layout key we wish to target with the prop object definition.
     *
     * We then set pre and post api response actions to trigger the 'showPleaseWait' value which will in turn show and
     * hide the overlay.
     *
     * "preRequestActions": [{
     *      "type": "SET_PAGE_VALUE",
     *      "identifier": "showPleaseWait",
     *      "value": true
     *  }]
     *
     * @returns {Array} children - the array of children, layout areas, to render within the page
     * @todo this could do with a bit of a clean up
     */
    getChildrenToRender = () => {
        const { rules, children, deployEnv } = this.props;
        if (rules === undefined || rules.layoutKeyVisibility === undefined) {
            return children;
        }

        // here we select the layout are key to ignore based on the rule result (showPleaseWait)
        let ignored = [];
        if (rules.layoutKeyVisibility.result) {
            const elementRule = rulesCache.getElementRuleForId(rules.layoutKeyVisibility.id);
            ignored = elementRule.props.layoutKeys || [];
        }

        const elements = children[0].filter((c) => {
            return !ignored.includes(c !== null && (c.props.layoutAreaKey || c.props.parentLayoutAreaKey));
        });

        if (deployEnv === 'dev' || deployEnv === 'local') {
            elements.push(this.getDevTools());
        }
        // filter out the 'hidden' layout element by layoutAreaKey OR parentLayoutAreaKey
        return [elements];
    };

    /**
     * Construct and return a Helmet component with settings from the page schema.
     * @returns {Node} - The Helmet component or null if there are no settings for this page.
     */
    getHelmet = () => {
        const { pageSchema, pageState } = this.props;
        let helmetComponent = null;
        // Sometimes pageState property and others are not passed to the Page component because of React router.
        if (pageSchema) {
            // also need to set a config var somewhere to allow / disallow this, we can't rely on dev and prod as we'll need preload on nxi / nxq
            // this is needed in order to avoid render a helmet component for each route tree instance.
            if (pageState && pageSchema.helmet) {
                // Combine the helmet global settings and the page specific in the page schema.
                const globalHelmet = pageContext.getProps().models.pageSchemaModel.getSettings().helmet;
                const helmetProps = { ...globalHelmet, ...pageSchema.helmet };
                const backgroundThemeColor = getComputedStyle(document.documentElement).getPropertyValue('--background-theme-color').trim();

                helmetProps.meta = [
                    ...(helmetProps.meta || []),
                    { name: 'theme-color', content: backgroundThemeColor }
                ];

                helmetComponent = <Helmet {...helmetProps} />;
            }
        }
        return helmetComponent;
    };

    /**
     * Check if there are preloaded state values in the query string and if so map these to Everest state keys
     * and set them on the form state.
     */
    setPreloadStateFromQS = () => {
        const { dispatch } = this.props;
        const { models, store } = pageContext.getProps();
        const query = locationQuerySelector(store.getState()).toJS();
        const preloadMap = models.pageSchemaModel.getSettings().preloadMap;
        const allowedValues = models.pageSchemaModel.getSettings().allowedPreloadValues;
        const preloadValueMap = models.pageSchemaModel.getSettings().preloadValueMap;
        const values = {};
        for (const key of Object.keys(query)) {
            if (key === 'preload' || key === 'tooling') {
                continue;
            }
            if (Object.keys(preloadMap).indexOf(key) >= 0) {
                // Only allow specific values for each preload key when coming from wordpress
                if (allowedValues[key].indexOf(query[key]) >= 0) {
                    const param = encodeURIComponent(query[key]);
                    let value = query[key];
                    // Map param value if key exists in preloadValueMap
                    if (Object.keys(preloadValueMap).indexOf(key) >= 0) {
                        value = preloadValueMap[key].hasOwnProperty(param) ? preloadValueMap[key][param][0] : undefined;
                    }
                    values[preloadMap[key]] = value;
                }
            }
        }

        if (!Object.keys(values).length) {
            // Don't set state if there are no values to set
            return;
        }

        if (Object.keys(values).length) {
            dispatch({
                type: 'SET_VALUES',
                values: nestFlatQuery(values),
                stateKey: 'vertical.quote'
            });
        }
    };

    /**
     * Perform a redirect if this page has a redirect rule
     */
    redirectIfNeeded = () => {
        const { rules } = this.props;
        if (rules && rules.redirect) {
            if (rules.redirect.result) {
                const elementRule = rulesCache.getElementRuleForId(rules.redirect.id);
                const browserHistory = pageContext.getProps().browserHistory;
                browserHistory.replace(elementRule.props.redirectTo);
            }
        }
    };

    /**
     * Renders the page based on props.children
     * @returns {Node} The rendered page
     */
    render = () => {
        const { isRedirecting, pageSchema } = this.props;
        // Don't construct children or helmet components if this is called during a redirect
        // as they won't be displayed
        const children = isRedirecting ? [] : this.getChildrenToRender();
        const helmetComponent = isRedirecting ? null : this.getHelmet();
        const pageName = pageSchema.name;
        const classes = `${pageName} ${pageStyles.root}`;
        return (
            <div className={classes}>
                {helmetComponent}
                {children}
            </div>
        );
    };

}

_Page.propTypes = {
    children: PropTypes.node,
    // pageSchema and pageState ARE required for pages that render components, however they will be undefined for other 'pages'
    // like /energy and /energy/journey
    pageSchema: PropTypes.object,
    pageState: PropTypes.object,
    brandCode: PropTypes.string,
    rules: PropTypes.object,
    dispatch: PropTypes.func.isRequired,
    reloadPage: PropTypes.bool,
    deployEnv: PropTypes.string,
    isSSR: PropTypes.bool.isRequired,
    location: PropTypes.object.isRequired,
    isRedirecting: PropTypes.bool.isRequired,
    isRouteChanged: PropTypes.bool.isRequired,
    vertical: PropTypes.string.isRequired,
    splitTrafficId: PropTypes.string,
    experimentId: PropTypes.string,
    journeyId: PropTypes.string,
    is11MonthHandover: PropTypes.bool
};

const recordGTM = (action, data) => {
    try {
        if (window && window.dataLayer) {
            window.dataLayer.push(data);
            logMessage(`${action} pushed to GTM`, data);
            return;
        }
        // Log an error using the reporter.
        logMessage(action, 'Google Data Layer is undefined');
    } catch (error) {
        // Sentry error logging
        logException(error, 'error', { action });
    }
};

/**
 * Map rules and snippets from state to props
 * @param {Object} state - The application state
 * @param {Object} props - The page props
 * @returns {Object} - The state values to be added to props for the page
 */
const mapStateToProps = (state, props) => {
    let rules = {};
    if (props.pageSchema && props.pageSchema.rules) {
        rules = elementRulesStateToProps()(state, {
            schema: {
                rules: props.pageSchema.rules
            },
            models: pageContext.getProps().models
        }).rules;
    }
    const reloadPage = state.getIn(['page', 'doReload']);
    const deployEnv = state.getIn(['page', 'deployEnv']);
    const vertical = verticalNameSelector(state);
    const splitTrafficId = splitTrafficIdSelector(state);
    const experimentId = experimentIdSelector(state);
    const pageName = pageNameSelector(state);
    const journeyId = state.getIn(['user', 'journey', 'transaction', 'journeyId']);
    const is11MonthHandover = state.getIn(['page', '11MonthsEnableCTA']) ?? false;
    const brandCode = state.getIn(['page', 'brand']);

    return {
        rules,
        reloadPage,
        deployEnv,
        vertical,
        splitTrafficId,
        experimentId,
        pageName,
        journeyId,
        is11MonthHandover,
        brandCode
    };
};

const Page = connect(mapStateToProps)(_Page);

export { Page };
